import { StyleSheet, css } from 'aphrodite';
import type { ComponentType, FC } from 'react';
import { useHistory } from 'react-router-dom';
import { isAdmin } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { Button } from '../../../web/components-v2/Button';
import { TabToggle } from '../../../web/components-v2/TabToggle';
import { THEME } from '../../../web/components-v2/Theme/theme';
import { IconBell, IconCalendarNote, IconClock, IconSettings, IconTrophy } from '../../focused-view/ui/icons';
import type { CoachingReviewViewMode } from './useCoachingReviewViewMode';

type NavItem = { key: string; label: string; Icon: ComponentType<{ size?: number }>; onClick: () => void };

interface IProps {
	viewMode: CoachingReviewViewMode;
	onViewModeChange: (mode: CoachingReviewViewMode) => void;
	onBackToCards?: () => void;
}

const VIEW_MODE_ITEMS = [
	{ value: 'list', label: 'List' },
	{ value: 'cards', label: 'Rep cards' },
];

/**
 * Slim light header for the AI Coaching Review page, matching the focused-view shell (it renders on
 * a bare route, so it owns its own chrome). Reuses the focused-view icon set + token-driven styling
 * (`var(--ink)` etc. from `focused-view/theme`, in scope via the page's `data-focused-view` wrapper).
 * Only the app-nav subset that makes sense for a review page — no dialer/queue/search or call
 * controls, no daily-pacing strip.
 */
export const AICoachingReviewTopBar: FC<IProps> = ({ viewMode, onViewModeChange, onBackToCards }) => {
	const history = useHistory();
	const userSession = useUserSession();

	const items: NavItem[] = [
		{ key: 'call-log', label: 'Call Log', Icon: IconClock, onClick: () => history.push('/call-log') },
		{ key: 'demos', label: 'Demos', Icon: IconCalendarNote, onClick: () => history.push('/demos') },
		{ key: 'follow-ups', label: 'Follow Ups', Icon: IconBell, onClick: () => history.push('/follow-ups') },
		{ key: 'leaderboard', label: 'Leaderboard', Icon: IconTrophy, onClick: () => history.push('/leaderboard') },
	];

	if (isAdmin(userSession.user)) {
		items.push({ key: 'settings', label: 'Settings', Icon: IconSettings, onClick: () => history.push('/settings') });
	}

	return (
		<header className={css(styles.bar)}>
			<div className={css(styles.left)}>
				{onBackToCards ? (
					<Button variant='ghost' size='sm' styles={[styles.back]} onClick={onBackToCards}>
						← Back to rep cards
					</Button>
				) : null}
				<nav className={css(styles.nav)} aria-label='AI coaching review navigation'>
					{items.map(({ key, label, Icon, onClick }) => (
						<Button
							key={key}
							variant='ghost'
							size='icon'
							styles={[styles.link]}
							onClick={onClick}
							title={label}
							aria-label={label}
						>
							<Icon size={18} />
						</Button>
					))}
				</nav>
			</div>
			<div className={css(styles.right)}>
				<TabToggle
					size='small'
					items={VIEW_MODE_ITEMS}
					value={viewMode}
					onValueChange={value => onViewModeChange(value as CoachingReviewViewMode)}
				/>
				<h1 className={css(styles.title)}>AI Coaching Review</h1>
			</div>
		</header>
	);
};

const styles = StyleSheet.create({
	bar: {
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 12,
		height: 52,
		padding: `0 ${THEME.spacing.md}px`,
		borderBottom: '1px solid var(--border)',
		background: 'var(--bg)',
	},
	left: {
		alignItems: 'center',
		display: 'flex',
		gap: THEME.spacing.sm,
		minWidth: 0,
	},
	nav: {
		display: 'flex',
		alignItems: 'center',
		gap: 2,
	},
	back: {
		flexShrink: 0,
	},
	right: {
		alignItems: 'center',
		display: 'flex',
		gap: THEME.spacing.md,
	},
	link: {
		width: 30,
		height: 30,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: THEME.common.borderRadius,
		background: 'transparent',
		color: 'var(--ink-muted)',
		border: 'none',
		cursor: 'pointer',
		transition: 'background 120ms, color 120ms',
		':hover': {
			background: 'var(--hover)',
			color: 'var(--ink)',
		},
	},
	title: {
		margin: 0,
		fontSize: 16,
		fontWeight: 600,
		color: 'var(--ink)',
	},
});
